import {
  type ChangeEvent,
  type ChangeEventHandler,
  type FocusEventHandler,
  type HTMLInputTypeAttribute,
  type JSX,
  type ReactNode,
  useRef,
} from "react";
import { IMaskInput } from "react-imask";

import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

export type InputControlProps = {
  autoFocus?: boolean;
  enableAutoComplete?: boolean;
  background?: string;
  color?: string;
  compact?: boolean;
  disabled?: boolean;
  error?: string;
  tooltipContent?: string;
  errorWithTouchedMode?: boolean;
  hint?: string;
  icon?: JSX.Element;
  isOptional?: boolean;
  label?: ReactNode | string | null;
  labelTag?: ReactNode;
  leftLabel?: ReactNode;
  leftLabelClassName?: string;
  mask?: string;
  name?: string;
  noDecimals?: boolean;
  onBlur?: FocusEventHandler;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  placeholder?: string | null;
  prefix?: string;
  textAlign?: "center" | "end" | "start";
  touched?: boolean;
  type: HTMLInputTypeAttribute;
  value: number | string | null | undefined;
  withBorder?: boolean;
  className?: string;
  warning?: string;
  inputClassName?: string;
  canCopyToAllField?: boolean;
  onCopyAllFields?: (value: string) => void;
  fitPlaceholder?: boolean;
  fullWidth?: boolean;
  constrainInputWidth?: boolean;
  maxLength?: number;
};

export function InputControl({
  autoFocus = false,
  background,
  color,
  compact = false,
  enableAutoComplete,
  disabled = false,
  error,
  tooltipContent,
  errorWithTouchedMode,
  hint,
  icon,
  isOptional,
  label,
  labelTag,
  leftLabel,
  leftLabelClassName,
  warning,
  mask,
  maxLength,
  name,
  onBlur,
  onChange,
  placeholder,
  prefix,
  textAlign = "start",
  touched,
  type,
  value,
  withBorder = false,
  className,
  inputClassName,
  onCopyAllFields,
  canCopyToAllField,
  fitPlaceholder = false,
  fullWidth = false,
  constrainInputWidth = false,
}: InputControlProps) {
  const errorVisible = isTruthy(errorWithTouchedMode)
    ? isTruthy(error) && touched
    : error;

  const inputRef = useRef<HTMLInputElement>(null);

  // Prepare the mask pattern with prefix if provided
  const maskPattern = isTruthy(prefix)
    ? `${prefix}${mask ?? ""}`
    : (mask ?? "");

  return (
    <div
      className={cnTwMerge(
        className,
        "group defaultTransition",
        fullWidth && "w-full",
      )}
    >
      <div
        className={cnTwMerge(
          "flex items-center justify-between",
          fullWidth && "w-full",
          constrainInputWidth && "max-w-[50%]",
        )}
      >
        <div
          className={cnTwMerge(
            "flex items-center justify-center gap-1",
            fullWidth && "w-full",
          )}
        >
          <FormLabel
            compact={compact}
            forName={name}
            hint={hint}
            isOptional={isOptional}
            label={label}
            tag={labelTag}
            tooltipContent={tooltipContent}
          />
        </div>

        {value && isFalsy(error) && !touched && canCopyToAllField ? (
          <p
            className="text-text/interactive shrink-0 block cursor-pointer body-small-medium mb-1"
            onClick={() => onCopyAllFields && onCopyAllFields(value as string)}
          >
            {m.formFields_copyToAll()}
          </p>
        ) : null}
      </div>
      <div
        className={cnTwMerge(
          "relative flex text-text/primary bg-background/primary rounded-companyBorderRadius border transition focus-within:border-brand-default defaultTransition active:bg-button/secondary/background/pressed",
          constrainInputWidth && "max-w-[50%]",
          compact ? "min-h-8" : "min-h-11",
          {
            "hover:border-brand-default": !disabled,
            "!border-border/negative": errorVisible,
            "border-border/primary": !!withBorder,
            "border-transparent": !withBorder,
            "text-text/secondary": color,
          },
        )}
      >
        {leftLabel ? (
          <label
            className={cnTwMerge(
              "shrink-0 flex items-center justify-center body-base-regular px-3 min-w-14",
              leftLabelClassName,
            )}
            data-testid="input-control-left-label"
            htmlFor={name}
          >
            <span>{leftLabel}</span>
          </label>
        ) : null}
        {icon ? (
          <div
            className={cnTwMerge(
              "flex items-center rounded-l-companyBorderRadius pl-2 group-hover:text-text/interactive defaultTransition",
              background,
            )}
          >
            {icon}
          </div>
        ) : null}
        <IMaskInput
          aria-label={name}
          autoComplete={isTruthy(enableAutoComplete) ? undefined : "nope"}
          autoFocus={autoFocus}
          className={cnTwMerge(
            "block w-full border-0 text-sm text-text/primary placeholder:text-text/disabled bg-background/primary bg-transparent",
            compact ? "py-1" : "py-[7px]",
            isTruthy(background) ? background : null,
            {
              "focus:border-transparent focus:ring-0 focus:ring-offset-0":
                !disabled,
              "rounded-companyBorderRadius": !icon && !leftLabel,
              "rounded-r-companyBorderRadius": icon,
              "cursor-not-allowed bg-background/component/10": disabled,
            },
            `text-${textAlign}`,
            inputClassName,
          )}
          data-testid={`${name}-input-control`}
          definitions={{
            // Default definitions that match react-input-mask behavior
            "9": /[0-9]/,
            a: /[a-zA-Z]/,
            "*": /[a-zA-Z0-9]/,
          }}
          disabled={disabled}
          id={name}
          inputRef={inputRef}
          lazy={false}
          mask={maskPattern}
          maxLength={maxLength}
          name={name}
          onAccept={(_, maskRef) => {
            if (onChange) {
              // Create a synthetic event to match the original onChange interface
              const syntheticEvent = {
                target: maskRef.el as unknown as HTMLInputElement,
                currentTarget: maskRef.el as unknown as HTMLInputElement,
              } as ChangeEvent<HTMLInputElement>;
              onChange(syntheticEvent);
            }
          }}
          onBlur={onBlur}
          onChange={onChange}
          onWheel={(event) => type === "number" && event.currentTarget.blur()}
          pattern={type === "number" ? "[0-9]*" : undefined}
          placeholder={
            placeholder !== "" && placeholder !== null ? placeholder : undefined
          }
          style={{
            fieldSizing: fitPlaceholder ? "content" : undefined,
            minWidth: fitPlaceholder ? "12em" : undefined,
          }}
          type={type}
          unmask={false} // Keep the mask format in the value
          value={value !== undefined && value !== null ? String(value) : ""}
        />
      </div>
      {isTruthy(errorVisible) ? (
        <ValidationErrorText dataTestId={error ?? "input-control"} withMargin>
          {error}
        </ValidationErrorText>
      ) : null}
      {isTruthy(warning) && isFalsy(errorVisible) ? (
        <ValidationErrorText dataTestId={warning} type="warning" withMargin>
          {warning}
        </ValidationErrorText>
      ) : null}
    </div>
  );
}
